import React from "react";
import { Button, Section, Text } from "@origin-digital/ods-core";
import { IconCancelCircle, IconSearch } from "@origin-digital/ods-icons";
import { useNavigate } from "react-router-dom";
import { Autocomplete as MuiAutocomplete, PaperProps } from "@mui/material";
import {
  AutocompleteChangeReason,
  AutocompleteRenderInputParams,
} from "@mui/material/Autocomplete";
import { PopperProps } from "@mui/material/Popper";
import { BASE_PATH } from "../../routes";
import {
  StyledPopper,
  Container,
  StyledPaper,
  StyledTextField,
  StyledSearchIconContainer,
  CancelButtonContainer,
  StyledCancelIconButton,
} from "./styles";

type Props = {
  setInputValue: (value: string) => void;
  onFocusChange: (state: boolean) => void;
  inputValue: string;
  suggestions: string[];
  placeholder?: string;
};

const PaperComponent = ({ children, ...props }: PaperProps) => {
  return (
    <Section backgroundColor="cardBackground" paddingY="none">
      <StyledPaper {...props}>{children}</StyledPaper>
    </Section>
  );
};

const PopperComponent = (props: PopperProps) => <StyledPopper {...props} />;

type SearchInputFieldProps = {
  placeholder: string;
  inputValue: string;
  onFocusChange: (state: boolean) => void;
  setInputValue: (value: string) => void;
};

type SearchInputProps = SearchInputFieldProps & {
  autocompleteParams: AutocompleteRenderInputParams;
};

const SearchInput = ({
  placeholder,
  inputValue,
  onFocusChange,
  setInputValue,
  autocompleteParams,
}: SearchInputProps): JSX.Element => {
  const { InputLabelProps, InputProps, inputProps, ...restParams } =
    autocompleteParams;

  return (
    <StyledTextField
      {...restParams}
      muiProps={{
        InputProps: {
          ...InputProps,
          startAdornment: (
            <StyledSearchIconContainer>
              <IconSearch color="grey300" />
            </StyledSearchIconContainer>
          ),
          endAdornment: (
            <ClearInputButton
              inputValue={inputValue}
              setInputValue={setInputValue}
            />
          ),
        },
        inputProps: { ...inputProps, "data-id": "search-input" },
        InputLabelProps,
      }}
      onFocus={() => onFocusChange(true)}
      onBlur={() => onFocusChange(false)}
      hideHelperTextSpace
      id="autocomplete-input"
      placeholder={placeholder}
      startIcon={<IconSearch color="grey300" />}
      variant="outlined"
      background="transparent"
    />
  );
};

type ClearInputButtonProps = {
  inputValue: string;
  setInputValue: (value: string) => void;
};

const ClearInputButton = ({
  inputValue,
  setInputValue,
}: ClearInputButtonProps): JSX.Element => (
  <StyledCancelIconButton
    data-id="clear-text-field"
    aria-label="Clear search text"
    aria-hidden={inputValue?.length === 0}
    disabled={inputValue?.length === 0}
    open={inputValue?.length > 0}
    onMouseDown={(e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
    }}
    onClick={() => setInputValue("")}
  >
    <IconCancelCircle color="neutralLight" />
  </StyledCancelIconButton>
);

const createRenderInput =
  (fieldProps: SearchInputFieldProps) =>
  (autocompleteParams: AutocompleteRenderInputParams): JSX.Element =>
    <SearchInput {...fieldProps} autocompleteParams={autocompleteParams} />;

type SuggestionOptionProps = {
  inputValue: string;
};

const SuggestionOption =
  ({ inputValue }: SuggestionOptionProps) =>
  (props: React.HTMLAttributes<HTMLLIElement>, option: string) =>
    (
      <li {...props} data-id="search-suggestion" key={option}>
        <Text component="span">
          {option}
          {option === inputValue ? "..." : ""}
        </Text>
      </li>
    );

export const Autocomplete = ({
  suggestions,
  inputValue,
  setInputValue,
  onFocusChange,
  placeholder = "What are you looking for?",
}: Props): JSX.Element => {
  const navigate = useNavigate();

  const options = [
    ...(inputValue ? [inputValue] : []),
    ...suggestions.filter((item) => item !== inputValue),
  ];

  const handleCancel = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    onFocusChange(false);
  };

  const handleChange = (
    _e: React.SyntheticEvent,
    value: string | null,
    reason: AutocompleteChangeReason
  ) => {
    if ((reason === "createOption" || reason === "selectOption") && value) {
      setInputValue(value);
      navigate(`${BASE_PATH}/search?term=${encodeURIComponent(value)}`);
      setTimeout(() => {
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
      }, 0);
    }
  };

  return (
    <Container>
      <MuiAutocomplete
        options={options}
        renderInput={createRenderInput({
          placeholder,
          inputValue,
          onFocusChange,
          setInputValue,
        })}
        PopperComponent={PopperComponent}
        PaperComponent={PaperComponent}
        renderOption={SuggestionOption({ inputValue })}
        inputValue={inputValue}
        freeSolo
        onChange={handleChange}
        onInputChange={(_e, value) => setInputValue(value || "")}
      />
      <CancelButtonContainer>
        <Button
          variant="text"
          color="secondaryB"
          size="small"
          data-id="cancel-input-focus"
          onClick={handleCancel}
        >
          Cancel
        </Button>
      </CancelButtonContainer>
    </Container>
  );
};
